import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCollection } from '@amzn/awsui-collection-hooks';
import Box from '@amzn/awsui-components-react/polaris/box';
import CollectionPreferences from '@amzn/awsui-components-react/polaris/collection-preferences';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import StatusIndicator, {
  StatusIndicatorProps,
} from '@amzn/awsui-components-react/polaris/status-indicator';
import Table from '@amzn/awsui-components-react/polaris/table';
import { Attendance, Event, Shift, Signup, WaitlistEntry } from '@amzn/red-velvet-api';
import { usePreferencesStrings } from '../../hooks/localization/preferencesStrings';
import { useLocalizedText } from '../../hooks/localizedText';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { SHIFT_WAITLIST, SignupSurface } from '../../utils/constants';
import { ColumnMap, useTableColumns, SHIFT_NAME_COLUMN_WIDTH_PX } from '../../hooks/tableColumns';
import { formatShiftDate, formatShiftTime } from '../../utils/dateFormatting';
import { useLocale } from '../../hooks/locale';
import { formatAttendanceTimeDisplay } from '../../utils/attendance/time';
import {
  buildShiftRow,
  getShiftLocationLabel,
  getVolunteerShiftSortComparators,
  isRowDimmed,
  isShiftFull,
  resolveLocationLabel,
  sortShiftRows,
} from '../../utils/volunteerEventShiftsTableUtils';
import {
  ShiftAction,
  ShiftStatus,
  UserShiftStatus,
  VolunteerEventShiftsTableProps,
  VolunteerShiftRow,
} from '../../utils/volunteerEventShiftsTableTypes';
import { CancelOrTrackActions, ShiftActions, ToggleWaitlist, TrackAttendanceLink } from './actions';
import { TruncatedCellText } from './truncatedCellText';

/** Default rows per page. The Event Details page passes a larger value (the carousel showed one row). */
export const DEFAULT_SHIFT_TABLE_PAGE_SIZE = 10;
/** Search shows a shorter default so one result card does not dominate the page (cce-7126). */
export const SEARCH_SHIFT_TABLE_PAGE_SIZE = 5;

/** Page-size options offered by the table's settings (gear) preferences. */
const SHIFT_TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50];
/** Search adds a 5 so its shorter default is selectable in the gear. */
export const SEARCH_SHIFT_TABLE_PAGE_SIZE_OPTIONS = [
  SEARCH_SHIFT_TABLE_PAGE_SIZE,
  ...SHIFT_TABLE_PAGE_SIZE_OPTIONS,
];

/** i18n root for this table's labels (a single-token top-level key, matching the sibling tables). */
const TABLE_I18N = 'volunteerEventShiftsTable';

/**
 * These labels live in the shifts bundle. The trailing `translation` records where the same keys sit
 * on the legacy side; their i18next resolution comes from the global `fallbackNS` (see localizedText).
 *
 * useTableColumns and usePreferencesStrings still read through react-i18next, so the column headers
 * and the settings-gear text stay English until those two hooks move to useLocalizedText. They are
 * given this array so they land on the right bundle when they do, rather than on common.
 */
const TABLE_NAMESPACES = ['shifts', 'translation'];

const SHIFT_STATUS_INDICATOR: Record<ShiftStatus, StatusIndicatorProps.Type> = {
  [ShiftStatus.UPCOMING]: 'success',
  [ShiftStatus.IN_PROGRESS]: 'in-progress',
  [ShiftStatus.DEADLINE_PAST]: 'warning',
  [ShiftStatus.PAST]: 'stopped',
};

const USER_STATUS_INDICATOR: Record<UserShiftStatus, StatusIndicatorProps.Type> = {
  [UserShiftStatus.SIGNED_UP]: 'success',
  [UserShiftStatus.ON_THE_WAITLIST]: 'pending',
  [UserShiftStatus.MISSING_ATTENDANCE]: 'warning',
  [UserShiftStatus.NOT_SIGNED_UP]: 'stopped',
  [UserShiftStatus.ATTENDED]: 'success',
  [UserShiftStatus.DID_NOT_ATTEND]: 'stopped',
};

/** Greys out a cell's content for full shifts (matches the dimmed full rows in the mocks). */
function CellText({ dimmed, children }: { dimmed: boolean; children: React.ReactNode }) {
  return dimmed ? (
    <Box color="text-status-inactive" data-testid="dimmed-cell">
      {children}
    </Box>
  ) : (
    <>{children}</>
  );
}

function ShiftStatusCell({ status }: { status: ShiftStatus }) {
  const { t } = useLocalizedText(TABLE_NAMESPACES);
  return (
    <StatusIndicator type={SHIFT_STATUS_INDICATOR[status]}>
      {t(`${TABLE_I18N}.shiftStatus.${status}`)}
    </StatusIndicator>
  );
}

function UserStatusCell({
  status,
  attendanceMinutes,
}: {
  status: UserShiftStatus;
  attendanceMinutes?: number;
}) {
  const { t } = useLocalizedText(TABLE_NAMESPACES);
  // ATTENDED shows the tracked duration, e.g. "Attended (1 hr 30 mins)"; the rest are static labels.
  const label =
    status === UserShiftStatus.ATTENDED
      ? t(`${TABLE_I18N}.userStatus.ATTENDED`, {
          time: formatAttendanceTimeDisplay(attendanceMinutes ?? 0, t),
        })
      : t(`${TABLE_I18N}.userStatus.${status}`);
  return <StatusIndicator type={USER_STATUS_INDICATOR[status]}>{label}</StatusIndicator>;
}

function LocationCell({
  shift,
  eventLocationLabel,
  virtualLabel,
}: {
  shift: Shift;
  eventLocationLabel: string;
  virtualLabel: string;
}) {
  return <>{getShiftLocationLabel(shift, eventLocationLabel, virtualLabel)}</>;
}

function TimeCell({ shift }: { shift: Shift }) {
  const { t } = useLocalizedText(TABLE_NAMESPACES);
  const locale = useLocale();
  const start = formatShiftTime(shift.startDateTime, undefined, locale);
  const end = formatShiftTime(shift.endDateTime, undefined, locale);
  if (start && end) return <>{`${start} - ${end}`}</>;
  if (start) return <>{start}</>;
  return <>{t(`${TABLE_I18N}.ongoing`)}</>;
}

function SpotsCell({ shift, dimmed }: { shift: Shift; dimmed: boolean }) {
  const { t } = useLocalizedText(TABLE_NAMESPACES);
  // maxSignup of 0/undefined means unlimited capacity.
  if (!shift.maxSignup)
    return <CellText dimmed={false}>{t(`${TABLE_I18N}.unlimitedSpots`)}</CellText>;
  const signedUp = shift.maxSignup - shift.signupsRemaining;
  const full = isShiftFull(shift);
  return (
    <SpaceBetween direction="horizontal" size="xs">
      <CellText dimmed={dimmed}>
        {t(`${TABLE_I18N}.spots`, { count: signedUp, max: shift.maxSignup })}
      </CellText>
      {full && <StatusIndicator type="warning">{t(`${TABLE_I18N}.full`)}</StatusIndicator>}
    </SpaceBetween>
  );
}

/**
 * Renders the row's single action, delegating to the existing reusable controls. getShiftAction is
 * the authority on which one shows, so each branch maps 1:1 to a control (ShiftActions already
 * bundles ToggleSignup + its complex-signup dialog; ToggleWaitlist owns the join dialog).
 */
function ActionCell({
  event,
  shift,
  action,
  signup,
  attendance,
  waitlistEntry,
  linkStyle,
  forceJoinWaitlist,
  forceLeaveWaitlist,
  forceCancelSignup,
  source,
}: {
  event: Event;
  shift: Shift;
  action: ShiftAction;
  signup?: Signup;
  attendance?: Attendance;
  waitlistEntry?: WaitlistEntry;
  linkStyle?: boolean;
  /** One-click join deep link matched this row (fires its current Sign Up / Join Waitlist action). */
  forceJoinWaitlist?: boolean;
  /** One-click leave deep link matched this row's ACTIVE waitlist entry. */
  forceLeaveWaitlist?: boolean;
  /** One-click cancel deep link matched this row (fires its Cancel sign up action). */
  forceCancelSignup?: boolean;
  source?: SignupSurface;
}) {
  switch (action) {
    case ShiftAction.CANCEL_OR_TRACK:
      return (
        <CancelOrTrackActions
          event={event}
          shift={shift}
          signup={signup}
          attendance={attendance}
          forceCancelSignup={forceCancelSignup}
        />
      );
    case ShiftAction.SIGN_UP:
    case ShiftAction.CANCEL_SIGNUP:
      return (
        <ShiftActions
          event={event}
          shift={shift}
          signup={signup}
          attendance={attendance}
          linkStyle={linkStyle}
          forceSignup={forceJoinWaitlist}
          forceCancelSignup={forceCancelSignup}
          source={source}
        />
      );
    case ShiftAction.TRACK_TIME:
      return (
        <TrackAttendanceLink
          shift={shift}
          signup={signup}
          attendance={attendance}
          linkStyle={linkStyle}
        />
      );
    case ShiftAction.JOIN_WAITLIST:
      return (
        <ToggleWaitlist
          event={event}
          shift={shift}
          isOnWaitlist={false}
          forceJoinWaitlist={forceJoinWaitlist}
        />
      );
    case ShiftAction.LEAVE_WAITLIST:
      return (
        <ToggleWaitlist
          event={event}
          shift={shift}
          isOnWaitlist
          waitlistEntry={waitlistEntry}
          forceLeaveWaitlist={forceLeaveWaitlist}
        />
      );
    default:
      return (
        <Box textAlign="center" color="text-status-inactive">
          -
        </Box>
      );
  }
}

/**
 * Volunteer-facing shift table — the counterpart to CoordinatorEventShiftsTable. Renders the event's
 * shifts with the volunteer's per-shift status and action. Reused on the Event Details page, the
 * Search result cards, and My Activity > Volunteer (callers pre-filter shifts and pass the user's
 * signups/attendances/waitlist entries).
 */
export function VolunteerEventShiftsTable(props: VolunteerEventShiftsTableProps) {
  const { t } = useLocalizedText(TABLE_NAMESPACES);
  const locale = useLocale();
  const {
    event,
    shifts,
    signups,
    attendances,
    waitlistEntries,
    pageSize = DEFAULT_SHIFT_TABLE_PAGE_SIZE,
    pageSizeOptions = SHIFT_TABLE_PAGE_SIZE_OPTIONS,
    statusFilter,
    forceJoinWaitlistShiftId,
    forceLeaveWaitlistEntryId,
    forceCancelShiftId,
    variant,
    header,
    source,
  } = props;

  const [searchParams] = useSearchParams();
  const features = useFeatures();
  // Gate the inline-link action style on ShiftWaitlist so only the new volunteer table renders link
  // actions (matching CoordinatorEventShiftsTable); the shared action controls keep their buttons in
  // their other consumers (the carousel, My Activity signup cards).
  const isShiftWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);

  // Freeze the default grouped order for the session so a status change (register / join / leave /
  // track) updates the row's content in place instead of re-floating it to a new group — which reads
  // as "register -> cancel -> register" and is worse across pages (phanhenr, CR-285081914). The grouped
  // order is recomputed only on a fresh start: a page refresh / remount, or navigating to a different
  // event (seededEventIdRef below). An explicit column sort (useCollection) still reorders live on top.
  const frozenOrderRef = useRef<string[] | null>(null);
  // The event this freeze was seeded for. If the table instance is reused for a different event (SPA
  // navigation between event pages re-renders rather than remounts), reset so the new event re-seeds
  // its own grouped order instead of inheriting the previous event's positions.
  const seededEventIdRef = useRef<string | undefined>(undefined);

  // Resolve each shift to a row (status + action) — always from the latest inputs so the status/action
  // cells stay current — then apply the optional per-page status filter. The row ORDER comes from the
  // frozen seed (below), not a re-sort, so a mid-session status change never moves the row.
  //
  // Seed contract: the frozen order is captured from the FIRST build that has rows, so the caller must
  // pass the user's signups/attendances/waitlistEntries alongside the shifts on that render (the Event
  // Details wrapper gates on all of them being fetched). Passing shifts before that data resolves would
  // freeze a pre-signup grouping for the session.
  const rows = useMemo(() => {
    const built = shifts.map((shift) =>
      buildShiftRow(
        shift,
        signups,
        waitlistEntries,
        attendances,
        Date.now(),
        isShiftWaitlistEnabled
      )
    );
    const filtered = statusFilter ? built.filter((row) => statusFilter(row.userStatus)) : built;

    // Reused for a different event -> drop the previous event's frozen order and re-seed below.
    if (seededEventIdRef.current !== event.eventId) {
      seededEventIdRef.current = event.eventId;
      frozenOrderRef.current = null;
    }
    // Seed the frozen order from the default grouping on the first build that has data.
    if (frozenOrderRef.current === null && filtered.length > 0) {
      frozenOrderRef.current = sortShiftRows(filtered).map((row) => row.shift.shiftId);
    }
    const frozenOrder = frozenOrderRef.current;
    if (!frozenOrder) return sortShiftRows(filtered); // no data yet — nothing to freeze

    // Keep seeded rows in their frozen positions; any shift added after the seed (rare — e.g. an
    // ingestor update mid-session) is not in the frozen order, so order those by the default grouping
    // and append them.
    const frozenRank = new Map(frozenOrder.map((shiftId, index) => [shiftId, index]));
    const seeded = filtered
      .filter((row) => frozenRank.has(row.shift.shiftId))
      // frozenRank.has (in the filter) guarantees a defined rank; the ?? fallback only satisfies the
      // type without a non-null assertion (unreachable — a missing id would sort to the tail of the
      // seeded rows).
      .sort(
        (a, b) =>
          (frozenRank.get(a.shift.shiftId) ?? frozenOrder.length) -
          (frozenRank.get(b.shift.shiftId) ?? frozenOrder.length)
      );
    const added = sortShiftRows(filtered.filter((row) => !frozenRank.has(row.shift.shiftId)));
    return [...seeded, ...added];
  }, [
    event.eventId,
    shifts,
    signups,
    waitlistEntries,
    attendances,
    statusFilter,
    isShiftWaitlistEnabled,
  ]);

  const virtualLabel = t(`${TABLE_I18N}.virtualLocation`);
  const eventLocationLabel = resolveLocationLabel(event.location, virtualLabel, '');

  const columnMap: ColumnMap<VolunteerShiftRow> = {
    shiftName: (row) => (
      <CellText dimmed={isRowDimmed(row)}>
        <TruncatedCellText text={row.shift.name} />
      </CellText>
    ),
    shiftStatus: (row) => <ShiftStatusCell status={row.shiftStatus} />,
    location: (row) => (
      <CellText dimmed={isRowDimmed(row)}>
        <LocationCell
          shift={row.shift}
          eventLocationLabel={eventLocationLabel}
          virtualLabel={virtualLabel}
        />
      </CellText>
    ),
    date: (row) => (
      <CellText dimmed={isRowDimmed(row)}>
        {formatShiftDate(row.shift.startDateTime, undefined, locale)}
      </CellText>
    ),
    time: (row) => (
      <CellText dimmed={isRowDimmed(row)}>
        <TimeCell shift={row.shift} />
      </CellText>
    ),
    spots: (row) => <SpotsCell shift={row.shift} dimmed={isRowDimmed(row)} />,
    yourStatus: (row) => (
      <UserStatusCell status={row.userStatus} attendanceMinutes={row.attendance?.minutes} />
    ),
    action: (row) => (
      <ActionCell
        event={event}
        shift={row.shift}
        action={row.action}
        signup={row.signup}
        attendance={row.attendance}
        waitlistEntry={row.waitlistEntry}
        linkStyle={isShiftWaitlistEnabled}
        source={source}
        forceJoinWaitlist={
          !forceDeliveredRef.current.join && row.shift.shiftId === forceJoinWaitlistShiftId
        }
        forceLeaveWaitlist={
          !forceDeliveredRef.current.leave &&
          forceLeaveWaitlistEntryId !== undefined &&
          row.waitlistEntry?.waitlistEntryId === forceLeaveWaitlistEntryId
        }
        forceCancelSignup={
          !forceDeliveredRef.current.cancel && row.shift.shiftId === forceCancelShiftId
        }
      />
    ),
  };

  const { columnDefinitions } = useTableColumns(
    columnMap,
    TABLE_I18N,
    TABLE_NAMESPACES,
    undefined,
    {
      shiftName: SHIFT_NAME_COLUMN_WIDTH_PX,
    }
  );
  // Attach a stable sortingField + comparator to each sortable column (Action has neither, so it
  // stays caret-less).
  const sortComparators = getVolunteerShiftSortComparators(eventLocationLabel, virtualLabel);
  const sortableColumnDefinitions = columnDefinitions.map((column) => {
    // Action has no comparator; sortComparators is typed Record<string, Fn> so the lookup is never
    // undefined to the compiler — check key presence with `in` rather than a truthiness check.
    const id = column.id;
    // useTableColumns rebuilds these column objects on every render. Cloudscape tracks the active
    // sort column by reference, so without a render-stable identity it loses the sorted column after
    // each re-render — the caret disappears and clicking the same header again restarts at ascending
    // instead of toggling to descending. A stable sortingField (the column id) gives the column a
    // render-independent identity so the toggle works, matching the sortable ambassadorDirectoryTable.
    return id && id in sortComparators
      ? { ...column, sortingField: id, sortingComparator: sortComparators[id] }
      : column;
  });

  const preferencesStrings = usePreferencesStrings(
    `${TABLE_I18N}.preferences`,
    pageSizeOptions,
    TABLE_NAMESPACES
  );
  // pageSize is the initial value; the settings (gear) preferences let the user change it per session.
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);

  const { items, paginationProps, collectionProps, actions } = useCollection(rows, {
    sorting: {},
    pagination: { pageSize: currentPageSize },
  });

  // A one-click deep link targets ONE shift, but the table paginates at currentPageSize rows. If the
  // target sits on a later page its row never mounts, so its control never fires and the email link
  // silently does nothing. The flat per-signup list this replaced avoided that by asking the server
  // for the target first (priorityShiftId); here the whole event is already in hand, so page to the
  // target instead. Runs before the delivery latch below can mark the flag delivered, because the
  // latch only fires once the target is among the mounted items. Inert without a flag, so the default
  // first-page view is unchanged.
  const forcePageAlignedRef = useRef(false);
  useEffect(() => {
    if (forcePageAlignedRef.current) return;
    if (!forceJoinWaitlistShiftId && !forceLeaveWaitlistEntryId && !forceCancelShiftId) return;
    const targetIndex = rows.findIndex(
      (row) =>
        (!!forceJoinWaitlistShiftId && row.shift.shiftId === forceJoinWaitlistShiftId) ||
        (!!forceLeaveWaitlistEntryId &&
          row.waitlistEntry?.waitlistEntryId === forceLeaveWaitlistEntryId) ||
        (!!forceCancelShiftId && row.shift.shiftId === forceCancelShiftId)
    );
    // Not resolved yet (data still loading) or targets another event — retry on the next build.
    if (targetIndex < 0) return;
    forcePageAlignedRef.current = true;
    actions.setCurrentPage(Math.floor(targetIndex / currentPageSize) + 1);
  }, [
    rows,
    currentPageSize,
    forceJoinWaitlistShiftId,
    forceLeaveWaitlistEntryId,
    forceCancelShiftId,
    actions,
  ]);

  // Deliver each one-click deep-link force flag at most ONCE per table mount. The once-guards inside
  // the action controls are per component instance, and a mutation-driven action flip swaps the
  // control family (ToggleSignup <-> ToggleWaitlist unmount/remount) — the URL flag outlives that,
  // so a fresh control would re-fire it (e.g. leaving after a join deep link would auto-rejoin the
  // waitlist). Latch on the flag's target appearing among the MOUNTED page items: effects run after
  // the commit in which the target row's control mounted (its own effect has already consumed the
  // flag), so from the next render the flag reads false and any later remount stays inert.
  const forceDeliveredRef = useRef({ join: false, leave: false, cancel: false });
  useEffect(() => {
    const delivered = forceDeliveredRef.current;
    if (
      forceJoinWaitlistShiftId &&
      items.some((row) => row.shift.shiftId === forceJoinWaitlistShiftId)
    ) {
      delivered.join = true;
    }
    if (
      forceLeaveWaitlistEntryId &&
      items.some((row) => row.waitlistEntry?.waitlistEntryId === forceLeaveWaitlistEntryId)
    ) {
      delivered.leave = true;
    }
    if (forceCancelShiftId && items.some((row) => row.shift.shiftId === forceCancelShiftId)) {
      delivered.cancel = true;
    }
  }, [items, forceJoinWaitlistShiftId, forceLeaveWaitlistEntryId, forceCancelShiftId]);

  // Default 'container' variant <Table> in a plain <div>, mirroring CoordinatorEventShiftsTable;
  // callers embedded in an existing card pass variant='embedded' (Search). Only the Shift Name column
  // carries a width (a best-effort layout hint — see SHIFT_NAME_COLUMN_WIDTH_PX); the rest, like the
  // coordinator, size to content — notably the sticky Action column, whose non-wrapping button (see
  // ToggleWaitlist) keeps it from collapsing. The table scrolls horizontally when space is tight.
  return (
    <div>
      <Table
        {...collectionProps}
        variant={variant}
        header={header}
        columnDefinitions={sortableColumnDefinitions}
        items={items}
        stickyColumns={{ last: 1 }}
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            {...preferencesStrings}
            preferences={{ pageSize: currentPageSize }}
            onConfirm={({ detail }) => setCurrentPageSize(detail.pageSize ?? pageSize)}
          />
        }
        data-testid="volunteer-event-shifts-table"
        empty={
          <Box textAlign="center" color="inherit">
            {t(`${TABLE_I18N}.empty`)}
          </Box>
        }
      />
    </div>
  );
}
